import Close from '@mui/icons-material/Close';
import Save from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { getV1CompaniesCompanyIdAccountsId } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Account, AccountCapacityTypeahead } from '~hooks/useAccount/models';
import VendorAccountForm, {
  VendorAccountFormHandler,
} from '~pages/Settings/Accounts/VendorAccountForm/VendorAccountForm';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface EditVendorDrawerProps {
  account?: AccountCapacityTypeahead;
  onClose: (updatedAccount?: Account) => void;
}

export default function EditVendorDrawer({
  account: accountProp,
  onClose,
}: EditVendorDrawerProps) {
  const isOpen = Boolean(accountProp?.id);

  const { ordersDispatchStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [vendorFormData, setVendorFormData] = useState<{ vendor: Account }>();

  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [hasApiResponseErrors, setHasApiResponseErrors] = useState(false);

  const vendorAccountFormRef = useRef<VendorAccountFormHandler>(null);

  useEffect(() => {
    if (isOpen && accountProp?.id) {
      getV1CompaniesCompanyIdAccountsId({
        path: { 'company-id': companyId, id: accountProp.id },
      })
        .then((response) => {
          const data = response?.data?.data;
          if (!data) return;
          const vendor = Account.parse(data);

          setVendorFormData({ vendor });
        })
        .catch((error) => {
          connection.handleRequestError(
            error,
            t('error_messages.account.failed_to_fetch_company_accounts'),
          );
          onClose();
        });
    }
  }, [accountProp?.id, companyId, isOpen]);

  useEffect(() => {
    return () => {
      vendorAccountFormRef.current?.resetForm();
      setVendorFormData(undefined);
    };
  }, [isOpen]);

  return (
    <Drawer
      anchor="right"
      onClose={() => onClose()}
      open={isOpen}
      variant="temporary"
      sx={{
        [`& .${drawerClasses.paper}`]: {
          height: 'calc(100dvh - var(--tread-agent-dock-height, 0px))',
          maxWidth: '600px',
          overflow: 'hidden',
          width: '80%',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {vendorFormData ? (
          <>
            <DrawerHeader onClose={() => onClose()} sx={{ flex: 0 }} />

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <VendorAccountForm
                stackedLayout
                ref={vendorAccountFormRef}
                account={vendorFormData.vendor}
                onAccountUpdated={(account: Account) => {
                  onClose(account);
                  ordersDispatchStore.setPendingFiltersUpdateAfterAssigneeUpdate({
                    label: account.name,
                    value: account.id,
                  });
                }}
                onApiResponseErrorsChanged={setHasApiResponseErrors}
                onSavingStatusChanged={setIsSavingAccount}
              />
            </Box>

            <DrawerFooter
              hasAsyncErrors={hasApiResponseErrors}
              isSaving={isSavingAccount}
              onCancel={() => onClose()}
              onSave={() => vendorAccountFormRef.current?.submitForm()}
              sx={{ flex: 0 }}
            />
          </>
        ) : (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            <LoadingSpinner isVisible />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

interface DrawerHeaderProps {
  onClose: () => void;
  sx?: SxProps<Theme>;
}

function DrawerHeader({ onClose, sx }: DrawerHeaderProps) {
  return (
    <Box
      sx={[
        (theme) => ({
          alignItems: 'center',
          borderBottom: `solid 1px ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: '16px',
          fontWeight: 600,
        })}
      >
        {t('administration.vendors.update_vendor')}
      </Typography>
      <IconButton
        color="inherit"
        size="small"
        onClick={onClose}
        data-ui="page-dispatch__edit-vendor-drawer__close-button"
      >
        <Close sx={{ width: 16, height: 16 }} />
      </IconButton>
    </Box>
  );
}

interface DrawerFooterProps {
  hasAsyncErrors?: boolean;
  isSaving?: boolean;
  onCancel: () => void;
  onSave: () => void;
  sx?: SxProps<Theme>;
}

function DrawerFooter({
  hasAsyncErrors,
  isSaving,
  onCancel,
  onSave,
  sx,
}: DrawerFooterProps) {
  return (
    <Box
      sx={[
        (theme) => ({
          alignItems: 'center',
          borderTop: `solid 1px ${theme.palette.divider}`,
          display: 'flex',
          gap: 1,
          height: '100%',
          justifyContent: 'flex-end',
          px: 2,
          py: 1,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {hasAsyncErrors && (
        <ShakyErrorIndicator
          message={t('administration.users.user_form.invalid_data_error_messages')}
        />
      )}

      <SmallButton
        color="secondary"
        disabled={isSaving}
        onClick={onCancel}
        variant="outlined"
      >
        {t('actions.cancel')}
      </SmallButton>

      <SmallLoadingButton
        loading={isSaving}
        loadingPosition="start"
        onClick={onSave}
        startIcon={isSaving ? <Save sx={{ color: 'transparent' }} /> : <></>}
        sx={isSaving ? {} : { '& .MuiButton-startIcon': { mx: 0 } }}
      >
        {t('actions.update')}
      </SmallLoadingButton>
    </Box>
  );
}
